import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "@mui/material";
import { useAuthStore } from "../store";
import { plural, formatPln } from "../utils/paymentFormat";
import "../assets/style/PendingSignupsBanner.css";

/**
 * In-app notification for the logged-in user's DUE online-payable balance —
 * past unpaid lessons only. Future admin-signups awaiting payment are NOT
 * surfaced here (they stay payable on `/moje-platnosci` but must not nudge the
 * user). Cash-on-site lessons (a cash-only lesson or the user's explicit
 * "Zapłać na miejscu" choice) are likewise excluded — they are settled in
 * person. Renders a fixed warning bar under the site header that deep-links
 * into the in-app `/moje-platnosci` settlement page.
 *
 * Renders nothing when the user is logged out, has no due online balance, or
 * dismissed it for this page view. The account-icon badge remains the
 * persistent indicator.
 */
const PendingSignupsBanner = () => {
  const navigate = useNavigate();
  const { isAuthenticated, pendingSignups } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    pendingSignups: state.pendingSignups,
  }));

  const [dismissed, setDismissed] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);

  // Pin the banner just below the fixed site header, tracking its height
  // across breakpoints / orientation changes.
  useEffect(() => {
    const measure = () => {
      const header = document.getElementById("header");
      setHeaderOffset(header ? header.offsetHeight : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Only the DUE online-payable balance drives the banner. Past unpaid lessons
  // always count; future admin-signups are excluded UNLESS they are a pass-
  // renewal nudge (`pass_renewable`), which the store keeps in the `due` bucket
  // so a "karnetowiec" with a lapsed pass is reminded before the lesson. Cash-
  // on-site lessons are always excluded so we never nudge online payment for
  // something settled in person.
  const count = pendingSignups?.due?.online_count || 0;
  const total = pendingSignups?.due?.online_total || 0;
  // When any due online lesson is a pass-renewal nudge (the user is a
  // "karnetowiec" for that category whose pass expired), switch the copy/CTA to
  // encourage renewing the pass rather than a one-off payment.
  const hasRenewable = pendingSignups?.due?.has_renewable || false;

  if (!isAuthenticated || count === 0 || dismissed) {
    return null;
  }

  const handleSettle = () => {
    navigate("/moje-platnosci");
  };

  return (
    <div
      className="pending-signups-banner"
      style={{ top: `${headerOffset}px` }}
    >
      <Alert
        severity="warning"
        className="pending-signups-banner__alert"
        onClose={() => setDismissed(true)}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            className="pending-signups-banner__button"
            onClick={handleSettle}
          >
            {hasRenewable ? "Odnów karnet" : "Opłać teraz"}
          </Button>
        }
      >
        {hasRenewable ? (
          <>
            Twój karnet wygasł. Masz {count}{" "}
            {plural(count, "termin", "terminy", "terminów")} do opłacenia na
            kwotę <strong>{formatPln(total)}</strong>. Odnów karnet, aby je
            pokryć.
          </>
        ) : (
          <>
            Masz {count}{" "}
            {plural(
              count,
              "nieopłacony termin",
              "nieopłacone terminy",
              "nieopłaconych terminów"
            )}{" "}
            zajęć na kwotę <strong>{formatPln(total)}</strong>. Przejdź, aby
            uregulować płatność.
          </>
        )}
      </Alert>
    </div>
  );
};

export default PendingSignupsBanner;
